import { apiCustomer } from "@/api"
import { useEffect, useState } from "react"

/**
 * Per-merchant toggles that gate customer-facing self-service actions.
 * Backed by `GET /merchants/:merchantId/customer-config` on
 * customer-service-gateway.
 */
type CustomerConfigResponse = {
  cancellationRequestEnabled: boolean
}

/**
 * Fetches the merchant's customer-facing config (today: whether the
 * "Estou com um problema" → cancellation-request flow is enabled).
 *
 * FAIL-OPEN by design: any error (network, timeout, 404, merchant with no
 * config row yet) resolves to `cancellationRequestEnabled: true` — a
 * missing/broken config must never block a legitimate cancellation. One
 * fetch per mount (no polling); pass `null` to skip until the merchant id
 * is known.
 */
export function useMerchantCustomerConfig(merchantId: string | null) {
  const [cancellationRequestEnabled, setCancellationRequestEnabled] = useState(true)
  const [loading, setLoading] = useState(Boolean(merchantId))

  useEffect(() => {
    if (!merchantId) {
      setCancellationRequestEnabled(true)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    async function fetchConfig() {
      try {
        const { data } = await apiCustomer.get<CustomerConfigResponse>(
          `/merchants/${merchantId}/customer-config`,
        )
        if (cancelled) return
        setCancellationRequestEnabled(data.cancellationRequestEnabled ?? true)
      } catch (error) {
        console.error(error)
        if (!cancelled) setCancellationRequestEnabled(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void fetchConfig()
    return () => {
      cancelled = true
    }
  }, [merchantId])

  return { cancellationRequestEnabled, loading }
}
